import { ChangeEvent, useCallback, useMemo, useRef } from 'react';

import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { Button } from 'UIPackage/MainModule/components/Button';

import styles from './XmlFieldActions.module.scss';

interface IXmlFieldActionsProps {
  disabled?: boolean;
  onFormat: () => void;
  onUpload: (content: string) => void;
  onUploadError: (error: string) => void;
  onValidate: () => void;
}

export const XmlFieldActions = ({ disabled, onFormat, onUpload, onUploadError, onValidate }: IXmlFieldActionsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      // eslint-disable-next-line no-param-reassign
      event.target.value = '';

      if (!file) {
        return;
      }

      const isXmlExtension = /\.xml$/i.test(file.name);

      if (!isXmlExtension) {
        onUploadError('Можно загрузить только файл с расширением .xml');

        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          onUpload(reader.result);

          return;
        }

        onUploadError('Не удалось прочитать содержимое файла');
      };

      reader.onerror = () => {
        onUploadError('Не удалось прочитать файл');
      };

      reader.readAsText(file);
    },
    [onUpload, onUploadError],
  );

  const items: IDropdownItem[] = useMemo(
    () => [
      {
        id: 'xml-format',
        content: (
          <DropdownItem onClick={onFormat} isDisabled={disabled}>
            Форматировать
          </DropdownItem>
        ),
      },
      {
        id: 'xml-validate',
        content: (
          <DropdownItem onClick={onValidate} isDisabled={disabled}>
            Валидировать
          </DropdownItem>
        ),
      },
      {
        id: 'xml-upload',
        content: (
          <DropdownItem onClick={handleUploadClick} isDisabled={disabled}>
            Загрузить файл
          </DropdownItem>
        ),
      },
    ],
    [disabled, handleUploadClick, onFormat, onValidate],
  );

  return (
    <div className={styles.XmlFieldActions}>
      <Dropdown items={items} disabled={disabled}>
        <Button
          className="shared-btn-add"
          text="Действия"
          type="button"
          variant="link"
          color="primary"
          id="xmlRequestActions"
          disabled={disabled}
        />
      </Dropdown>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml,application/xml,text/xml"
        className={styles.HiddenFileInput}
        onChange={handleFileChange}
      />
    </div>
  );
};
